/**
 * Auth slice — session user + access token.
 *
 * CP3: every action is a pure `(state, input) => state` function. No fetch,
 * no storage, no `Date.now()`. Token persistence (localStorage on web, secure
 * keystore on native) is the orchestrator's job through an adapter.
 *
 * Ported from legacy `useStore.ts` (`user`, `sessionToken`, `setUser`).
 */

import type { User } from '../types/user';

export interface AuthState {
  /** Authenticated user or null when logged out. */
  user: User | null;
  /**
   * Short-lived JWT access token. Null when logged out. Refresh tokens live
   * in an HttpOnly cookie (web) or the secure keystore (native) — never
   * here. Orchestrators wire the TokenProvider to that storage.
   */
  sessionToken: string | null;
}

export const initialAuthState: AuthState = {
  user: null,
  sessionToken: null,
};

// ---------------------------------------------------------------------------
// Actions (pure reducers)
// ---------------------------------------------------------------------------

/**
 * Set or clear the authenticated user + token in one atomic transition.
 * Passing `null, null` is the logout transition.
 */
export function setUser(
  state: AuthState,
  input: { user: User | null; sessionToken: string | null }
): AuthState {
  return {
    ...state,
    user: input.user,
    sessionToken: input.sessionToken,
  };
}
